import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Spinner } from "@fluentui/react-components";
import { Articoli, CategorieAcquisto, GestoriAcquisti, RichiesteAcquisto, Servizi, Stampanti } from "@app/schema";
import {
  GraphClient,
  ListRepository,
  SiteUsers,
  getSiteByUrl,
  getTeamSite,
  type SiteInfo,
} from "@app/sp-data";
import { graphToken } from "../auth/msal";
import { config, DEMO } from "../config";
import { useHost } from "../teams/TeamsProvider";
import { ErrorState } from "../components/ErrorState";

/**
 * Client Graph condiviso. In modalità demo le chiamate sono servite da un
 * finto Graph in memoria (caricato solo in quel caso).
 */
export const graph = new GraphClient({
  getToken: graphToken,
  fetchImpl: __DEMO__
    ? async (input, init) => (await import("../demo/mockGraph")).mockFetch(String(input), init)
    : undefined,
});

export interface Me {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail?: string;
}

export interface Roles {
  gestore: boolean;
  coordinatore: boolean;
}

/** Tutto ciò che le pagine usano per leggere/scrivere dati. */
export interface DataServices {
  site: SiteInfo;
  me: Me;
  roles: Roles;
  richieste: ListRepository<typeof RichiesteAcquisto>;
  servizi: ListRepository<typeof Servizi>;
  categorie: ListRepository<typeof CategorieAcquisto>;
  gestori: ListRepository<typeof GestoriAcquisti>;
  stampanti: ListRepository<typeof Stampanti>;
  articoli: ListRepository<typeof Articoli>;
  users: SiteUsers;
}

const DataContext = createContext<DataServices | undefined>(undefined);

export function useData(): DataServices {
  const v = useContext(DataContext);
  if (!v) throw new Error("useData() fuori da <DataProvider>");
  return v;
}

/**
 * Sito dei dati, in ordine di priorità:
 *  1. VITE_SITE_URL (sito fisso dell'app: la scheda funziona in qualsiasi canale);
 *  2. parametro `siteId` nell'URL (scritto dalla pagina di configurazione);
 *  3. sito del team corrente;
 *  4. VITE_DEV_SITE_URL (sviluppo nel browser).
 */
async function resolveSite(groupId?: string): Promise<SiteInfo> {
  if (config.siteUrl) return getSiteByUrl(graph, config.siteUrl);
  const siteId = new URLSearchParams(window.location.search).get("siteId");
  if (siteId) return graph.request<SiteInfo>(`/sites/${siteId}?$select=id,displayName,webUrl`);
  if (groupId) return getTeamSite(graph, groupId);
  if (config.devSiteUrl) return getSiteByUrl(graph, config.devSiteUrl);
  throw new Error("Nessun sito configurato per questa app.");
}

/**
 * Ruoli dall'appartenenza ai gruppi di sicurezza (funziona con qualsiasi licenza,
 * non richiede l'assegnazione dei gruppi all'Enterprise app).
 */
async function resolveRoles(): Promise<Roles> {
  const ids = [config.groups.gestori, config.groups.coordinatori].filter((x): x is string => !!x);
  if (!ids.length) return { gestore: true, coordinatore: true }; // nessun gruppo configurato: tutto abilitato
  const res = await graph.request<{ value: string[] }>("/me/checkMemberGroups", {
    method: "POST",
    body: { groupIds: ids },
  });
  return {
    gestore: !!config.groups.gestori && res.value.includes(config.groups.gestori),
    coordinatore: !!config.groups.coordinatori && res.value.includes(config.groups.coordinatori),
  };
}

export function DataProvider({ children }: { children: ReactNode }) {
  const { groupId } = useHost();
  const [state, setState] = useState<{ site: SiteInfo; me: Me; roles: Roles }>();
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    Promise.all([
      resolveSite(groupId),
      graph.request<Me>("/me?$select=id,displayName,userPrincipalName,mail"),
      resolveRoles(),
    ]).then(([site, me, roles]) => setState({ site, me, roles }), setError);
  }, [groupId]);

  const services = useMemo<DataServices | undefined>(
    () =>
      state && {
        ...state,
        richieste: new ListRepository(graph, state.site.id, RichiesteAcquisto),
        servizi: new ListRepository(graph, state.site.id, Servizi),
        categorie: new ListRepository(graph, state.site.id, CategorieAcquisto),
        gestori: new ListRepository(graph, state.site.id, GestoriAcquisti),
        stampanti: new ListRepository(graph, state.site.id, Stampanti),
        articoli: new ListRepository(graph, state.site.id, Articoli),
        users: new SiteUsers(graph, state.site.id),
      },
    [state],
  );

  if (error) return <ErrorState error={error} />;
  if (!services) return <Spinner style={{ marginTop: 64 }} label="Caricamento…" />;
  return <DataContext.Provider value={services}>{children}</DataContext.Provider>;
}
